import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_CLARIFICATIONS, DEFAULT_COMMERCIAL_EXCLUSIONS } from "@/lib/estimateProposal";

export interface EstimateProposalPreset {
  id: string;
  company_id: string;
  name: string;
  job_type: string | null;
  clarifications: string[];
  exclusions: string[];
  acceptance_text: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

const TABLE = "estimate_proposal_presets" as any;

export function useProposalPresets() {
  return useQuery<EstimateProposalPreset[]>({
    queryKey: ["estimate_proposal_presets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLE)
        .select("*")
        .order("is_default", { ascending: false })
        .order("name");
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        ...r,
        clarifications: Array.isArray(r.clarifications) ? r.clarifications : [],
        exclusions: Array.isArray(r.exclusions) ? r.exclusions : [],
      }));
    },
  });
}

export function useCreatePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<EstimateProposalPreset, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase.from(TABLE).insert(input as any).select("*").single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate_proposal_presets"] }),
  });
}

export function useUpdatePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<EstimateProposalPreset> & { id: string }) => {
      const { data, error } = await supabase.from(TABLE).update(input as any).eq("id", id).select("*").single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate_proposal_presets"] }),
  });
}

export function useDeletePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(TABLE).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["estimate_proposal_presets"] }),
  });
}

/** Find the best matching preset for a job type. Falls back to default preset. */
export function findPresetForJobType(
  presets: EstimateProposalPreset[],
  jobType: string | null,
): EstimateProposalPreset | null {
  if (!presets.length) return null;
  if (jobType) {
    const match = presets.find((p) => p.job_type === jobType);
    if (match) return match;
  }
  const defaultPreset = presets.find((p) => p.is_default);
  if (defaultPreset) return defaultPreset;
  const generalPreset = presets.find((p) => !p.job_type);
  return generalPreset ?? null;
}

export const SEED_PRESETS: Omit<EstimateProposalPreset, "id" | "company_id" | "created_at" | "updated_at">[] = [
  {
    name: "General Default",
    job_type: null,
    clarifications: DEFAULT_CLARIFICATIONS,
    exclusions: DEFAULT_COMMERCIAL_EXCLUSIONS,
    acceptance_text: "The undersigned accepts this Proposal and authorizes the work described herein. Payment terms are Net 30 from date of invoice unless otherwise specified in the governing subcontract agreement. This Proposal shall be deemed incorporated into any resulting contract or work order.",
    is_default: true,
  },
  {
    name: "Flooring",
    job_type: "flooring",
    clarifications: [
      ...DEFAULT_CLARIFICATIONS,
      "Subfloor must be clean, level, and structurally sound. Moisture testing by others.",
      "Furniture moving, appliance disconnect/reconnect, and baseboard work not included unless noted.",
    ],
    exclusions: [
      ...DEFAULT_COMMERCIAL_EXCLUSIONS,
      "Floor leveling beyond ¼\" per 10 LF deviation",
      "Asbestos or lead testing and abatement",
      "Furniture moving, disconnect/reconnect of appliances",
    ],
    acceptance_text: null,
    is_default: false,
  },
  {
    name: "Roofing",
    job_type: "roofing",
    clarifications: [
      ...DEFAULT_CLARIFICATIONS,
      "Estimate based on visual inspection. Decking condition to be verified upon tear-off.",
      "Dumpster placement location must be accessible and approved by property owner.",
    ],
    exclusions: [
      ...DEFAULT_COMMERCIAL_EXCLUSIONS,
      "Decking replacement beyond standard sheathing repair",
      "Skylight, chimney, or HVAC curb replacement",
      "Gutter, fascia, or soffit work unless expressly noted",
    ],
    acceptance_text: null,
    is_default: false,
  },
];
